import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePlan } from '../../contexts/PlanContext';
import ConversationList from './ConversationList';
import CrewPaneEmpty from './CrewPaneEmpty';
import NewChatPicker from './NewChatPicker';
import ChatThread from './ChatThread';
import ChatInviteModal from './ChatInviteModal';
import ManageMembersPanel from './ManageMembersPanel';
import SidebarFooter from './SidebarFooter';
import { PaneRail, PaneResizeHandle, PaneToggleRow } from './PaneChrome';
import { ACTIVITY_BAR_WIDTH, SIDE_PANE_RAIL, paneVars } from '../../constants/layout';
import { useWorkstation } from '../../contexts/WorkstationContext';
import RenderTracker from '../studio/RenderTracker';
import AssetExplorer from '../studio/AssetExplorer';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useChatSessions } from './useChatSessions';
import { toggleConversationReaction, bookmarkConversationMessage, deleteConversationMessage, type ConversationSummary, type ProactiveLevel } from '../../services/conversationService';
import { exportConversationArtifact, downloadArtifact } from '../../services/artifactService';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { useWallet } from '../../contexts/WalletContext';
import { useOptionalToast } from '../../contexts/NotificationContext';
import type { ChatSession, MemberSpec } from './types';

/**
 * NAME WHAT IS DESTROYED (the pattern AssetExplorer's delete already sets). A crew chat holds everything the
 * creator and those agents worked out together and there is no undo, so the dialog says whose chat it is, that
 * the conversation goes with it, and — when it is the open production's crew — what the production is left
 * without. A generic "are you sure?" is cheaper and tells the creator nothing.
 */
function deleteChatPrompt(chat: ChatSession | null, isCrew: boolean): { title: string; description: string } {
  const name = chat?.title || 'this chat';
  const names = (chat?.members || []).map((m) => m.name);
  const who = names.length
    ? `your conversation with ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` and ${names.length - 3} more` : ''}`
    : 'the whole conversation';
  const production = isCrew
    ? ' The production itself is safe — but it has no crew chat until you recruit someone again.'
    : '';
  return {
    title: `Delete ${name}?`,
    description: `${name} will be deleted permanently, and ${who} — every message in it — goes with it. This cannot be undone.${production}`,
  };
}

/**
 * Chat-first dashboard — the focused workspace: a conversation list, a group of
 * 1–8 specialist contacts per chat, and the message stream. Deliberately minimal;
 * generative-UI widgets render inline in the stream (see MessageBubble).
 */
const ChatWorkspace: React.FC = () => {
  const { sessions, loading, sending, respondingIds, activity, reads, markRead, watch, createSession, deleteSession, send, applyUpdate, applyReactions, patchMessage, removeMessage } = useChatSessions();
  const { user, signOut, updateAlias } = useAuth();
  const { showError } = useOptionalToast();
  const { effectiveTheme, setThemeMode } = useTheme();
  const { planTier } = usePlan();
  const { refresh: refreshWallet } = useWallet();
  const isDesktop = useIsDesktop();
  const branded = planTier === 'FREE'; // FREE exports carry the wisetree.ai watermark
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { slug: slugParam } = useParams<{ slug: string }>();
  /** Which production the URL is on, so the explorer can show it as selected. */
  const productionSlug = typeof window !== 'undefined'
    ? (window.location.pathname.match(/^\/dashboard\/([^/]+)/)?.[1] || null)
    : null;
  /**
   * Which STAGE of that production, so the explorer can tell "in this episode" from "on this episode's
   * project" — they share an address and only this segment separates them (BUGS.md 2026-08-30).
   */
  const productionStage = typeof window !== 'undefined'
    ? (window.location.pathname.match(/^\/dashboard\/[^/]+\/([^/]+)/)?.[1] || null)
    : null;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pickerOpen, setPickerOpen] = useState(false);
  // FR4 (mobile): chats are SECONDARY navigation. The launchpad is the default surface; this flips true
  // only when the user explicitly opens the chat list, and back to false when they leave it. Desktop keeps
  // the list as a permanent sidebar and ignores this flag.
  const [chatsOpen, setChatsOpen] = useState(false);
  const [chatInviteOpen, setChatInviteOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  /**
   * The crew chat of the production currently open in the Studio (RF3-WORKSTATION slices A–C). The Studio
   * reports it on load and on the recruit that founds it; null = nobody has been recruited yet, so the right
   * pane rests on its contacts level with a line teaching where crew comes from.
   */
  const [crewChatId, setCrewChatId] = useState<string | null>(null);
  // Message pending deletion — drives the confirm dialog (replaces a native window.confirm).
  const [deleteTarget, setDeleteTarget] = useState<{ conversationId: string; messageId: string } | null>(null);
  // Chat pending deletion — same consistent ConfirmDialog (the sidebar bin no longer deletes instantly).
  const [deleteChatId, setDeleteChatId] = useState<string | null>(null);
  /**
   * BOTH WORKSTATION PANES, from one hook (RF3-WORKSTATION slice E). Same width token, same fold, same
   * draggable border, both remembered across reloads — the owner asked for panes that are the same width and
   * behave the same way, and the only way to guarantee that is to give them the same implementation.
   * Desktop-only: on mobile the list-detail flip handles space and there is no right pane at all.
   */
  const { left: leftPane, right: rightPane } = useWorkstation();
  const collapsed = leftPane.collapsed;
  /**
   * Is the right pane currently hiding the open chat? Only on the workstation — a /group deep link gives the
   * chat the whole screen, where folding is not a thing. This drives the unread bookkeeping below: a folded
   * pane must not quietly mark the crew's replies as seen.
   */
  const chatHidden = isDesktop && rightPane.collapsed && !pathname.startsWith('/group');

  // Deep link: /group/{slug} pre-selects that chat (resolved against the user's own sessions,
  // so it's member-safe — you only have slugs of chats you're in). Falls back to id match.
  useEffect(() => {
    if (!slugParam || !sessions.length) return;
    const match = sessions.find((s) => s.slug === slugParam || s.id === slugParam);
    if (match && match.id !== selectedId) setSelectedId(match.id);
  }, [slugParam, sessions, selectedId]);

  // FR4: the STUDIO LAUNCHPAD is the front door, not a chat. We deliberately do NOT auto-open the most
  // recent conversation on /dashboard — landing a creator in an old chat buries the thing they came to do
  // (make a video) behind a message stream. A chat opens only when explicitly chosen or deep-linked
  // (/group/{slug}), so /dashboard always renders StudioHome.

  useEffect(() => watch(selectedId), [selectedId, watch]);

  // A chat is unread when its live last-activity is newer than the user's last-read marker. The
  // currently-open chat is never unread — keep it marked read as its activity advances (new replies).
  // A FOLDED pane shows nothing, so the chat inside it is not "open" for this purpose — its replies stay
  // unread and light the rail's dot. Without this a creator who folded the pane would never learn the crew
  // answered, which is exactly the silence the fold is supposed to be safe from.
  const visibleChatId = chatHidden ? null : selectedId;
  const unreadIds = useMemo(() => {
    const set = new Set<string>();
    for (const [id, updated] of Object.entries(activity)) {
      if (id !== visibleChatId && updated > (reads[id] || 0)) set.add(id);
    }
    return set;
  }, [activity, reads, visibleChatId]);
  useEffect(() => {
    if (visibleChatId) markRead(visibleChatId, activity[visibleChatId] || Date.now());
  }, [visibleChatId, activity, markRead]);

  const session = sessions.find((s) => s.id === selectedId) || null;
  /**
   * AN EMPTY CREW PANE FOLDS ITSELF (TYPE-5, owner 2026-10-09). With nobody staffed the pane only says "Nobody is
   * on this episode yet", and at full width that sentence took a quarter of the screen while the step bar cut the
   * series title short. So while it is empty it shows as its rail (folded, never gone: the rail's button opens it),
   * and the creator's own choice wins for as long as it stays empty. Staffing a crew restores the remembered fold.
   */
  const crewEmpty = isDesktop && !session;
  const [emptyPaneOpened, setEmptyPaneOpened] = useState(false);
  useEffect(() => { if (!crewEmpty) setEmptyPaneOpened(false); }, [crewEmpty]);

  /** Select a chat AND reflect it in the URL (/group/{slug}) so it's shareable; null → list.
   *  `slugHint` lets a just-created chat navigate by its slug before it's in `sessions` state;
   *  otherwise we resolve the slug from state, falling back to the id (the route resolves both). */
  const selectChat = (id: string | null, slugHint?: string) => {
    setSelectedId(id);
    // Closing a chat returns to the LAUNCHPAD (FR4), not the chat list — the creator's home is the Studio.
    if (!id) { setChatsOpen(false); navigate('/dashboard', { replace: true }); return; }
    const s = sessions.find((x) => x.id === id);
    navigate(`/group/${slugHint || s?.slug || id}`, { replace: true });
  };

  /**
   * RIGHT PANE navigation (desktop): picking a contact walks the pane from level 1 to level 2 IN PLACE — it
   * must not jump the whole app to /group, which would abandon the production the creator is working on.
   */
  const selectContact = (id: string) => setSelectedId(id);

  /** Level 2 → level 1. A /group deep link would re-select the chat instantly, so return to the workstation. */
  const backToContacts = () => {
    setSelectedId(null);
    if (slugParam) navigate('/dashboard', { replace: true });
  };

  const handleCreate = async (members: MemberSpec[], name?: string, proactiveLevel?: ProactiveLevel) => {
    const created = await createSession(members, name, proactiveLevel);
    // On the workstation a new crew opens in the right pane; on mobile it takes over the screen as before.
    if (created) { if (isDesktop) selectContact(created.id); else selectChat(created.id, created.slug); }
    setPickerOpen(false);
  };

  /**
   * The Studio tells us which crew chat the open production owns. A chat the server just founded is not in
   * `sessions` yet (that list loads once), so seat it first, then land the right pane on it.
   */
  const handleCrewChatResolved = useCallback((info: { conversationId: string | null; conversation?: ConversationSummary }) => {
    if (info.conversation) applyUpdate(info.conversation);
    setCrewChatId(info.conversationId);
    setSelectedId(info.conversationId);
  }, [applyUpdate]);

  /**
   * WHO IS ON THE OPEN PRODUCTION'S CREW, read from the crew CHAT rather than from whatever chat is on screen.
   * The Studio derives its seats (the writer, the director) from this roster, so it must not empty out just
   * because the creator stepped into a different conversation in the pane.
   */
  const crewMemberIds = useMemo(() => {
    const crew = crewChatId ? sessions.find((s) => s.id === crewChatId) : null;
    return (crew?.members || []).map((m) => m.profile_id).filter((id): id is string => !!id);
  }, [sessions, crewChatId]);

  // Consume a "start a chat with this agent" intent stashed by the public /agent/{slug} page — works whether the
  // visitor was already signed in or just logged in (they land here either way). Creates the 1-1 and opens it.
  const startIntentRef = useRef(false);
  useEffect(() => {
    if (startIntentRef.current || loading) return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('wt_start_agent'); } catch { raw = null; }
    if (!raw) return;
    startIntentRef.current = true;
    try { sessionStorage.removeItem('wt_start_agent'); } catch { /* ignore */ }
    let a: { id?: string; name?: string; persona?: string; avatar_icon?: string } | null = null;
    try { a = JSON.parse(raw); } catch { return; }
    if (!a?.id) return;
    const member: MemberSpec = { id: a.id, name: a.name || a.id, persona: a.persona || '', avatar_icon: a.avatar_icon || 'user', profile_id: a.id };
    // Reuse an existing 1-1 with this agent (a solo-member chat, no other humans joined) instead of
    // creating a fresh one every visit — take the user back to their ongoing conversation.
    const existing = sessions.find((s) =>
      (s.members?.length === 1) &&
      (s.members[0].profile_id === a!.id || s.members[0].id === a!.id) &&
      ((s.humanMembers?.length || 0) === 0));
    if (existing) {
      setSelectedId(existing.id);
      navigate(`/group/${existing.slug || existing.id}`, { replace: true });
      return;
    }
    void (async () => {
      const created = await createSession([member], a?.name || undefined);
      if (created) { setSelectedId(created.id); navigate(`/group/${created.slug || created.id}`, { replace: true }); }
    })();
  }, [loading, sessions, createSession, navigate]);

  /** What the delete confirm says — resolved from the row the creator actually aimed at. */
  const chatPrompt = deleteChatPrompt(sessions.find((s) => s.id === deleteChatId) || null, !!deleteChatId && deleteChatId === crewChatId);

  return (
    <div className="flex h-full w-full overflow-hidden bg-white dark:bg-black">
      {/* List pane — full width on mobile; fixed sidebar on desktop. Hidden on mobile
          once a chat is open (the thread takes over). On desktop it collapses to a NAMED STRIP (owner
          2026-08-24: folded, it was "not showing anything") — the pane still says what it is and how to get
          it back, and the activity bar's productions icon restores it too. */}
      {collapsed && isDesktop ? (
        <PaneRail side="left" onExpand={leftPane.toggle}>
          {/* A 20-40 minute render must not become invisible because a pane was folded (screenshot79). */}
          <RenderTracker dot />
        </PaneRail>
      ) : (
        // FR4: on MOBILE the conversation list is secondary navigation, not the front door — it shows only
        // when the user explicitly opens chats (`chatsOpen`), never as the default /dashboard surface.
        // On desktop it stays the persistent sidebar.
        <div
          ref={leftPane.paneRef}
          /* ONE TOKEN, BOTH PANES (owner 2026-08-16: "same width, and prefer to be narrower"). The width
             arrives as a CSS variable rather than a Tailwind literal because a dragged pane changes it on
             every pointer move — and because the same value has to reach the Studio's reservation. */
          style={paneVars(leftPane.width)}
          className={`${selectedId || !chatsOpen ? 'hidden md:flex' : 'flex'} w-full md:w-[var(--wt-pane)] shrink-0 h-full`}
        >
          {/* THE LEFT PANE IS THE CREATOR'S WORK (owner 2026-08-14), not a contact list. A chat list made sense
              when the product was a group chat; in a studio the thing you navigate is your series, episodes
              and videos. The crew CHAT is the secondary right-hand pane now, so its list is demoted below the
              explorer rather than owning the pane. */}
          <div className="flex w-full min-w-0 flex-col">
            {isDesktop ? (
              /* THE WORKSTATION LEFT PANE IS PURELY ASSET NAVIGATION (owner 2026-08-16). Every chat surface
                 has moved to the right pane; what stays is the explorer, the render chip, and the account
                 footer the owner kept here (screenshot81). Tracker + footer sit INSIDE the region collapse
                 governs, so folding the pane folds the render chip with it (screenshot79). */
              <aside className="relative flex h-full w-full min-w-0 flex-col border-r border-secondary-200 bg-white dark:border-secondary-600 dark:bg-black">
                {/* The toggle faces the canvas it yields space to — top-RIGHT here, top-LEFT on the chat pane.
                    Both are the SAME shared button (owner 2026-08-21: they must not differ in size). */}
                <PaneResizeHandle side="left" {...leftPane.resize} />
                <PaneToggleRow side="left" onCollapse={leftPane.toggle} />
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <AssetExplorer activeSlug={productionSlug} activeStage={productionStage} />
                </div>
                {/* A render outlives the screen it was started on (20-40 minutes), so its progress lives in
                    the persistent chrome rather than inside the Studio. Nothing rendering → renders nothing.
                    The ACCOUNT no longer sits here: identity moved to the activity bar so this pane is the
                    creator's work and nothing else (owner 2026-08-23). */}
                <RenderTracker className="shrink-0 border-t border-secondary-200 p-2 dark:border-secondary-600" />
              </aside>
            ) : (
              /* MOBILE is untouched (RF3-DEVICE): the explorer over the secondary chat list, reached from the
                 launchpad. There is no right pane on a phone, so the list has to stay reachable here. */
              <>
                <div className="shrink-0 border-b border-secondary-200 px-1 dark:border-secondary-600">
                  <AssetExplorer activeSlug={productionSlug} activeStage={productionStage} />
                </div>
                <div className="min-h-0 flex-1">
                  <ConversationList
                    sessions={sessions}
                    loading={loading}
                    selectedId={selectedId}
                    unreadIds={unreadIds}
                    onSelect={selectChat}
                    onNew={() => setPickerOpen(true)}
                    onBackToStudio={() => setChatsOpen(false)}
                    onDelete={(id) => setDeleteChatId(id)}
                    footer={
                      <>
                        <RenderTracker className="mb-2" />
                        <SidebarFooter
                          name={user?.name || ''}
                          email={user?.email || ''}
                          avatar={user?.avatar}
                          alias={user?.alias}
                          theme={effectiveTheme === 'dark' ? 'dark' : 'light'}
                          onToggleTheme={() => setThemeMode(effectiveTheme === 'dark' ? 'light' : 'dark')}
                          onSignOut={() => signOut()}
                          onSaveAlias={(a) => updateAlias(a)}
                        />
                      </>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Thread pane — the DEFAULT surface. With no chat selected it renders the Studio launchpad
          (StudioHome), which is the front door on mobile AND desktop (FR4). It hides on mobile only while
          the user is browsing the secondary chat list. The back button (mobile) returns to the launchpad. */}
      <div className={`${selectedId || !chatsOpen ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 h-full`}>
        <ChatThread
          session={session}
          /* 'rail' is now the ACTIVITY BAR's 3rem (the explorer no longer leaves a rail of its own), and
             'open' is that column plus the explorer beside it — so the canvas starts exactly where the
             chrome ends whichever state the pane is in. */
          sidebarVisible={isDesktop ? (collapsed ? 'rail' : 'open') : undefined}
          sidebarWidth={`calc(${ACTIVITY_BAR_WIDTH} + ${collapsed ? SIDE_PANE_RAIL : leftPane.width})`}
          /* THE RIGHT PANE'S state lives here beside the left one's, so the two cannot drift apart and both
             survive a reload. Its chrome is rendered by ChatThread, which owns the pane's two levels. */
          rightPane={{
            open: crewEmpty ? emptyPaneOpened : !rightPane.collapsed,
            width: rightPane.width,
            unread: unreadIds.size > 0,
            onToggle: crewEmpty ? () => setEmptyPaneOpened((v) => !v) : rightPane.toggle,
            resize: rightPane.resize,
            paneRef: rightPane.paneRef,
          }}
          panesDragging={leftPane.dragging || rightPane.dragging}
          sending={sending}
          respondingIds={respondingIds}
          myUid={user?.id}
          isOwner={!!session && session.ownerId === user?.id}
          onUpdated={(c) => applyUpdate(c)}
          onWalletChanged={refreshWallet}
          branded={branded}
          onBack={isDesktop ? backToContacts : () => selectChat(null)}
          onOpenChats={() => setChatsOpen(true)}
          crewChatId={crewChatId}
          crewMemberIds={crewMemberIds}
          onCrewChatResolved={handleCrewChatResolved}
          /* THE RIGHT PANE IS THE OPEN PRODUCTION'S CREW, and nothing else (owner 2026-08-23). The chat
             LIST and its "New crew" button are gone: which crew you are talking to follows what you opened on
             the left, and who is in it follows who you hired. So the only thing the host still hands in is
             what to say before anyone has been recruited. */
          paneEmptyState={isDesktop ? <CrewPaneEmpty productionOpen={!!productionSlug} /> : undefined}
          onSend={async (text, targetIds, opts) => { if (session) { await send(session, text, targetIds, opts); refreshWallet(); } }}
          onExport={async (kind, format) => { if (session) await exportConversationArtifact(session.id, kind, format); }}
          onDownloadArtifact={(id) => { downloadArtifact(id); }}
          onReact={session ? (messageId, emoji) => {
            // Optimistic toggle (so the chip shows instantly), then persist + reconcile with the
            // server's authoritative reaction list. Reverts on failure.
            const me = user?.id || '';
            const myName = user?.name || user?.email || 'You';
            const msg = session.messages.find((m) => m.id === messageId);
            const cur = msg?.reactions || [];
            const has = cur.some((r) => r.by === me && r.emoji === emoji);
            const optimistic = has ? cur.filter((r) => !(r.by === me && r.emoji === emoji)) : [...cur, { emoji, by: me, name: myName }];
            applyReactions(session.id, messageId, optimistic);
            toggleConversationReaction(session.id, messageId, emoji)
              .then((r) => {
                applyReactions(session.id, messageId, r.reactions);
                // A positive reaction still grows rapport (Link) server-side — no user-facing toast.
              })
              .catch(() => applyReactions(session.id, messageId, cur));
          } : undefined}
          onBookmark={session ? (messageId, bookmarked) => {
            patchMessage(session.id, messageId, { bookmarked });   // optimistic
            bookmarkConversationMessage(session.id, messageId, bookmarked)
              .catch(() => patchMessage(session.id, messageId, { bookmarked: !bookmarked }));
          } : undefined}
          onDeleteMessage={session ? (messageId) => setDeleteTarget({ conversationId: session.id, messageId }) : undefined}
          onInvite={session ? () => setChatInviteOpen(true) : undefined}
          onManage={session ? () => setManageOpen(true) : undefined}
        />
      </div>

      <NewChatPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onCreate={handleCreate} />
      <ChatInviteModal open={chatInviteOpen} conversationId={session?.id || null} title={session?.title} onClose={() => setChatInviteOpen(false)} asPage={!isDesktop} />
      <ManageMembersPanel
        open={manageOpen}
        session={session}
        isOwner={!!session && session.ownerId === user?.id}
        onWalletChanged={refreshWallet}
        asPage={!isDesktop}
        onClose={() => setManageOpen(false)}
        onUpdated={(c) => applyUpdate(c)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete this message?"
        description="This can’t be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => {
          if (!deleteTarget) return;
          const { conversationId, messageId } = deleteTarget;
          removeMessage(conversationId, messageId); // optimistic; the realtime listener restores it if the delete fails
          deleteConversationMessage(conversationId, messageId).catch(() => { /* listener restores */ });
          setDeleteTarget(null);
        }}
      />
      {/* DELETE, NOT ARCHIVE (owner 2026-08-23) — the row's bin now destroys the chat, so the words have to
          carry the weight. Monochrome, like every other confirm: colour is for state, not for asking. */}
      <ConfirmDialog
        open={!!deleteChatId}
        onOpenChange={(o) => { if (!o) setDeleteChatId(null); }}
        title={chatPrompt.title}
        description={chatPrompt.description}
        confirmText="Delete it"
        cancelText="Keep it"
        variant="destructive"
        onConfirm={() => {
          const id = deleteChatId;
          if (!id) return;
          const name = sessions.find((s) => s.id === id)?.title || 'That chat';
          // Nothing may be left pointing at a chat that no longer exists: the pane falls back to its contacts
          // level, and a production whose crew chat this was is told it has none (the next recruit founds a
          // fresh one server-side, which is exactly how a deleted crew chat self-heals).
          if (id === selectedId) setSelectedId(null);
          if (id === crewChatId) setCrewChatId(null);
          deleteSession(id).catch(() => showError('Delete failed', `${name} could not be deleted, so it is back in your list. Please try again.`));
        }}
      />
    </div>
  );
};

export default ChatWorkspace;
